import { inject, Injectable, PLATFORM_ID, signal, computed, WritableSignal, Signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { User } from '../models/user.model';
import { Permissions } from '../enums/permissions.enum';

const STORAGE_KEYS = {
  USER_ROLE: 'userRole',
  AVAILABLE_ROLES: 'availableUserRoles',
  ACTIVE_CLINIC_ID: 'activeClinicId',
  CLINIC_ID: 'clinicId',
  USER_PROFILE: 'userProfile',
  USER_PERMISSIONS: 'user_permissions',
} as const;

@Injectable({ providedIn: 'root' })
export class UserStateService {
  private readonly platformId = inject(PLATFORM_ID);
  public readonly isBrowser = isPlatformBrowser(this.platformId);

  // State Signals
  readonly userRole: WritableSignal<string | null> = signal<string | null>(this.readInitialRole());
  readonly availableRoles: WritableSignal<string[]> = signal<string[]>(this.readInitialAvailableRoles());
  readonly activeClinicId: WritableSignal<string | null> = signal<string | null>(this.readInitialClinicId());
  readonly userProfile: WritableSignal<User | null> = signal<User | null>(this.readInitialUserProfile());
  readonly permissions: WritableSignal<Permissions[]> = signal<Permissions[]>(this.readInitialPermissions());

  // Computed Signals
  readonly activeRole: Signal<string> = computed(() => this.userRole() ?? 'DOCTOR');
  readonly userId: Signal<string | null> = computed(() => this.userProfile()?.id ?? null);

  readonly isDoctor: Signal<boolean> = computed(() => {
    const role = this.userRole();
    const available = this.availableRoles();
    return role === 'DOCTOR' || available.includes('DOCTOR');
  });

  readonly isManager: Signal<boolean> = computed(() => {
    const role = this.userRole();
    const available = this.availableRoles();
    return role === 'MANAGER' || available.includes('MANAGER');
  });

  readonly isReception: Signal<boolean> = computed(() => {
    const role = this.userRole();
    const available = this.availableRoles();
    return role === 'RECEPTION' || available.includes('RECEPTION');
  });

  // State Mutators with Storage Synchronization
  setUserRole(role: string | null): void {
    this.userRole.set(role);
    if (!this.isBrowser) return;
    if (role) {
      localStorage.setItem(STORAGE_KEYS.USER_ROLE, role);
    } else {
      localStorage.removeItem(STORAGE_KEYS.USER_ROLE);
    }
  }

  setAvailableRoles(roles: string[]): void {
    this.availableRoles.set(roles);
    if (!this.isBrowser) return;
    localStorage.setItem(STORAGE_KEYS.AVAILABLE_ROLES, JSON.stringify(roles));
  }

  setActiveClinicId(clinicId: string | null): void {
    this.activeClinicId.set(clinicId);
    if (!this.isBrowser) return;
    if (clinicId) {
      localStorage.setItem(STORAGE_KEYS.ACTIVE_CLINIC_ID, clinicId);
      localStorage.setItem(STORAGE_KEYS.CLINIC_ID, clinicId);
      sessionStorage.setItem(STORAGE_KEYS.ACTIVE_CLINIC_ID, clinicId);
      sessionStorage.setItem(STORAGE_KEYS.CLINIC_ID, clinicId);
    } else {
      localStorage.removeItem(STORAGE_KEYS.ACTIVE_CLINIC_ID);
      localStorage.removeItem(STORAGE_KEYS.CLINIC_ID);
      sessionStorage.removeItem(STORAGE_KEYS.ACTIVE_CLINIC_ID);
      sessionStorage.removeItem(STORAGE_KEYS.CLINIC_ID);
    }
  }

  setUserProfile(profile: User | null): void {
    this.userProfile.set(profile);
    if (!this.isBrowser) return;
    if (profile) {
      localStorage.setItem(STORAGE_KEYS.USER_PROFILE, JSON.stringify(profile));
    } else {
      localStorage.removeItem(STORAGE_KEYS.USER_PROFILE);
    }
  }

  setPermissions(permissions: Permissions[]): void {
    this.permissions.set(permissions);
    if (!this.isBrowser) return;
    localStorage.setItem(STORAGE_KEYS.USER_PERMISSIONS, JSON.stringify(permissions));
  }

  clearState(): void {
    this.userRole.set(null);
    this.availableRoles.set([]);
    this.activeClinicId.set(null);
    this.userProfile.set(null);
    this.permissions.set([]);

    if (!this.isBrowser) return;
    localStorage.removeItem(STORAGE_KEYS.USER_ROLE);
    localStorage.removeItem(STORAGE_KEYS.AVAILABLE_ROLES);
    localStorage.removeItem(STORAGE_KEYS.ACTIVE_CLINIC_ID);
    localStorage.removeItem(STORAGE_KEYS.CLINIC_ID);
    localStorage.removeItem(STORAGE_KEYS.USER_PROFILE);
    localStorage.removeItem(STORAGE_KEYS.USER_PERMISSIONS);
    sessionStorage.removeItem(STORAGE_KEYS.ACTIVE_CLINIC_ID);
    sessionStorage.removeItem(STORAGE_KEYS.CLINIC_ID);
  }

  // Initializers (Browser-safe)
  private readInitialRole(): string | null {
    if (!this.isBrowser) return null;
    return localStorage.getItem(STORAGE_KEYS.USER_ROLE);
  }

  private readInitialAvailableRoles(): string[] {
    if (!this.isBrowser) return [];
    const stored = localStorage.getItem(STORAGE_KEYS.AVAILABLE_ROLES);
    if (!stored) return [];
    try {
      return JSON.parse(stored) as string[];
    } catch {
      return [];
    }
  }

  private readInitialClinicId(): string | null {
    if (!this.isBrowser) return null;
    return localStorage.getItem(STORAGE_KEYS.ACTIVE_CLINIC_ID) || localStorage.getItem(STORAGE_KEYS.CLINIC_ID);
  }

  private readInitialUserProfile(): User | null {
    if (!this.isBrowser) return null;
    const stored = localStorage.getItem(STORAGE_KEYS.USER_PROFILE);
    if (!stored) return null;
    try {
      return JSON.parse(stored) as User;
    } catch {
      return null;
    }
  }

  private readInitialPermissions(): Permissions[] {
    if (!this.isBrowser) return [];
    const stored = localStorage.getItem(STORAGE_KEYS.USER_PERMISSIONS);
    if (!stored) return [];
    try {
      return JSON.parse(stored) as Permissions[];
    } catch {
      return [];
    }
  }
}
